import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, Phone, Mail } from 'lucide-react';
import { Button } from './ui/button';

const Layout = ({ children }) => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const location = useLocation();

  // Scroll to top when route changes
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname]);

  const navLinks = [
    { path: '/', label: 'Home' },
    { path: '/about', label: 'About' },
    { path: '/products', label: 'Products' },
    { path: '/eximstack', label: 'EximStack' },
    { path: '/verified-directory', label: 'Verified Directory' },
    { path: '/quality-compliance', label: 'Quality & Compliance' },
    { path: '/markets', label: 'Markets' },
    { path: '/gulfood', label: 'Gulfood 2026' },
    { path: '/contact', label: 'Contact' }
  ];

  const isActive = (path) => location.pathname === path;

  return (
    <div className="min-h-screen flex flex-col">
      {/* Header */}
      <header className="bg-white shadow-sm sticky top-0 z-50">
        <div className="container mx-auto px-4">
          <div className="flex items-center justify-between py-4">
            <Link to="/" className="flex flex-col items-start">
              <img 
                src="https://customer-assets.emergentagent.com/job_spanx-export/artifacts/xlq8jb39_SPANXPORT%20Logo%20Master.png" 
                alt="SPANXPORT" 
                className="h-12"
              />
              <p className="text-[9.5px] text-[#918f90] mt-0.5 font-bold tracking-[0.42em]" style={{ fontFamily: 'Montserrat, sans-serif', fontWeight: 700 }}>
                Powering Intelligent Global Trade
              </p>
            </Link>

            {/* Desktop Navigation */}
            <nav className="hidden lg:flex items-center space-x-6">
              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  className={`text-sm font-medium transition-colors hover:text-[#F47D20] ${
                    isActive(link.path) ? 'text-[#F47D20]' : 'text-[#263776]'
                  }`}
                >
                  {link.label}
                </Link>
              ))}
            </nav>

            {/* Mobile Menu Button */}
            <button
              className="lg:hidden p-2"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            >
              {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>

          {/* Mobile Navigation */}
          {mobileMenuOpen && (
            <nav className="lg:hidden pb-4">
              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  className={`block py-2 text-sm font-medium transition-colors hover:text-[#F47D20] ${
                    isActive(link.path) ? 'text-[#F47D20]' : 'text-[#263776]'
                  }`}
                  onClick={() => setMobileMenuOpen(false)}
                >
                  {link.label}
                </Link>
              ))}
            </nav>
          )}
        </div>
      </header>

      {/* Main Content */}
      <main className="flex-grow">{children}</main>

      {/* Footer */}
      <footer className="bg-[#263776] text-white">
        <div className="container mx-auto px-4 py-12">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {/* Company Info */}
            <div>
              <div className="mb-4">
                <img 
                  src="https://customer-assets.emergentagent.com/job_spanx-export/artifacts/xlq8jb39_SPANXPORT%20Logo%20Master.png" 
                  alt="SPANXPORT" 
                  className="h-10 mb-1 brightness-0 invert"
                />
                <p className="text-[8px] text-slate-300 font-bold tracking-[0.42em]" style={{ fontFamily: 'Montserrat, sans-serif', fontWeight: 700 }}>
                  Powering Intelligent Global Trade
                </p>
              </div>
              <p className="text-slate-200 mb-4 text-sm">
                Tech-enabled B2B trading partner for food commodities with verified supply, QA gates, and export documentation readiness.
              </p>
              <div className="flex items-center space-x-2 mb-2">
                <Mail size={16} />
                <a href="mailto:admin@spanxport.com" className="text-slate-200 hover:text-[#F47D20] transition-colors text-sm">
                  admin@spanxport.com
                </a>
              </div>
              <div className="flex items-center space-x-2">
                <Phone size={16} />
                <a href="https://wa.me/918291234503" className="text-slate-200 hover:text-[#F47D20] transition-colors text-sm">
                  +91-829-12345-03
                </a>
              </div>
            </div>

            {/* Quick Links */}
            <div>
              <h4 className="text-lg font-semibold mb-4">Quick Links</h4>
              <ul className="space-y-2">
                <li><Link to="/products" className="text-slate-200 hover:text-[#F47D20] transition-colors text-sm">Products</Link></li>
                <li><Link to="/eximstack" className="text-slate-200 hover:text-[#F47D20] transition-colors text-sm">EximStack</Link></li>
                <li><Link to="/verified-directory" className="text-slate-200 hover:text-[#F47D20] transition-colors text-sm">Verified Directory</Link></li>
                <li><Link to="/quality-compliance" className="text-slate-200 hover:text-[#F47D20] transition-colors text-sm">Quality & Compliance</Link></li>
              </ul>
            </div>

            {/* Markets */}
            <div>
              <h4 className="text-lg font-semibold mb-4">Markets We Serve</h4>
              <ul className="space-y-2 text-slate-200 text-sm">
                <li>Africa</li>
                <li>Asia-Pacific</li>
                <li>GCC</li>
                <li>LATAM</li>
                <li>South Asia</li>
                <li>UK & EU</li>
                <li>US & Canada</li>
              </ul>
            </div>
          </div>

          <div className="border-t border-slate-600 mt-8 pt-8 text-center text-slate-300 text-sm">
            <p>&copy; {new Date().getFullYear()} SPANXPORT Solutions Private Limited. All rights reserved.</p>
            <Link to="/admin/login" className="text-slate-500 hover:text-slate-300 text-xs mt-2 inline-block transition-colors">
              Admin
            </Link>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Layout;
